// Состояние приложения (zustand): контент, профили детей, настройки, аккаунт, прогресс.
import { create } from "zustand";
import { Api } from "../lib/api.ts";
import { audio } from "../lib/audio.ts";
import { CacheStorageBlobs, ContentStore, TrustError, type LoadedContent } from "../lib/content/store.ts";
import { db, requestPersistence, type Child } from "../lib/db.ts";
import { onEvents, setRelease } from "../lib/events.ts";
import { computeStatuses, type PrincipleStatus } from "../lib/progress.ts";
import { SyncEngine } from "../lib/sync.ts";

export type Settings = {
  sessionLimitMin: number;
  sound: boolean;
  theme: "system" | "light" | "dark";
  lockHoldSec: number;
};

export const defaultSettings: Settings = { sessionLimitMin: 10, sound: true, theme: "system", lockHoldSec: 3 };

export type Account = { id?: string; email?: string; anonymous: boolean };

type AppState = {
  booted: boolean;
  content: LoadedContent | null;
  contentState: "idle" | "loading" | "ready" | "error";
  contentError?: string;
  progress: { done: number; total: number } | null;
  online: boolean;
  onboarded: boolean;
  children: Child[];
  activeChildId: string | null;
  settings: Settings;
  account: Account;
  statuses: Record<string, Record<string, PrincipleStatus>>;
  lastSyncAt?: number;
  updateReady: boolean;
};

export const api = new Api(db);
export const contentStore = new ContentStore(api, db, typeof caches !== "undefined" ? new CacheStorageBlobs() : (undefined as never), __TRUST_ROOT__);
export const sync = new SyncEngine(api, db, () => void reloadLocal());

export const useApp = create<AppState>(() => ({
  booted: false,
  content: null,
  contentState: "idle",
  progress: null,
  online: typeof navigator === "undefined" ? true : navigator.onLine,
  onboarded: false,
  children: [],
  activeChildId: null,
  settings: defaultSettings,
  account: { anonymous: true },
  statuses: {},
  updateReady: false,
}));

export function activeChild(): Child | undefined {
  const s = useApp.getState();
  return s.children.find((c) => c.id === s.activeChildId) ?? s.children[0];
}

/** Перечитать локальные данные (дети, прогресс) из IndexedDB. */
export async function reloadLocal(): Promise<void> {
  const children = await db.children.toArray();
  children.sort((a, b) => a.id.localeCompare(b.id));
  const statuses: AppState["statuses"] = {};
  for (const c of children) {
    const ev = await db.events.where("childId").equals(c.id).toArray();
    try {
      statuses[c.id] = await computeStatuses(ev);
    } catch {
      statuses[c.id] = {};
    }
  }
  const activeChildId = (await db.get<string>("activeChildId")) ?? children[0]?.id ?? null;
  useApp.setState({ children, statuses, activeChildId, lastSyncAt: await db.get<number>("lastSyncAt") });
}

export async function saveSettings(patch: Partial<Settings>): Promise<void> {
  const settings = { ...useApp.getState().settings, ...patch };
  await db.set("settings", settings);
  audio.enabled = settings.sound;
  applyTheme(settings.theme);
  useApp.setState({ settings });
}

function applyTheme(t: Settings["theme"]) {
  if (t === "system") document.documentElement.removeAttribute("data-theme");
  else document.documentElement.setAttribute("data-theme", t);
}

/** Загрузить применённый релиз (офлайн), затем обновить с сервера. */
export async function refreshContent(): Promise<void> {
  const local = await contentStore.load().catch(() => null);
  if (local) {
    setRelease(local.releaseId);
    useApp.setState({ content: local, contentState: "ready" });
  } else {
    useApp.setState({ contentState: "loading" });
  }
  try {
    const changed = await contentStore.refresh((done, total) => useApp.setState({ progress: { done, total } }));
    if (changed || !local) {
      const fresh = await contentStore.load();
      setRelease(fresh?.releaseId);
      useApp.setState({ content: fresh, contentState: fresh ? "ready" : "error", progress: null });
    } else {
      useApp.setState({ progress: null });
    }
  } catch (e) {
    const msg = e instanceof TrustError ? `Контент не прошёл проверку подписи: ${e.message}` : "Нет связи с сервером";
    if (e instanceof TrustError) console.error(e);
    useApp.setState({ contentState: local ? "ready" : "error", contentError: msg, progress: null });
  }
}

export async function boot(): Promise<void> {
  const settings = { ...defaultSettings, ...((await db.get<Settings>("settings")) ?? {}) };
  audio.enabled = settings.sound;
  applyTheme(settings.theme);
  const onboarded = Boolean(await db.get<boolean>("onboarded"));
  const account = (await db.get<Account>("account")) ?? { anonymous: true };
  useApp.setState({ settings, onboarded, account });
  await reloadLocal();
  useApp.setState({ booted: true });
  void requestPersistence();
  window.addEventListener("online", () => {
    useApp.setState({ online: true });
    void refreshContent();
  });
  window.addEventListener("offline", () => useApp.setState({ online: false }));
  onEvents(() => void reloadLocal());
  sync.start();
  void refreshContent();
  // Манифест — раз в 6 часов (ARCHITECTURE 6.4).
  setInterval(() => void refreshContent(), 6 * 3600 * 1000);
}
